import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { ticketsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const TRANSITIONS = {
  open: ['in_progress', 'closed'],
  in_progress: ['resolved', 'open', 'closed'],
  resolved: ['closed', 'in_progress'],
  closed: [],
};

const REASSIGN_ROLES = new Set(['admin', 'teacher_admin']);

function formatBytes(size) {
  const n = Number(size) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageAttachment(attachment) {
  const mime = String(attachment?.mime_type || '');
  const name = String(attachment?.original_name || '');
  return mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);
}

function TicketAttachmentCard({ ticketId, attachment }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  const showImage = isImageAttachment(attachment);

  useEffect(() => {
    if (!showImage) return undefined;
    let cancelled = false;
    let objectUrl = '';
    ticketsAPI
      .downloadAttachment(ticketId, attachment.id)
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ticketId, attachment.id, showImage]);

  const download = async () => {
    try {
      const res = await ticketsAPI.downloadAttachment(ticketId, attachment.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error('Could not download attachment');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1, width: { xs: '100%', sm: 220 } }}>
      {src && !failed ? (
        <Box
          component="img"
          src={src}
          alt={attachment.original_name || 'Attachment'}
          onError={() => setFailed(true)}
          sx={{ width: '100%', maxHeight: 180, objectFit: 'contain', bgcolor: 'action.hover', borderRadius: 0.5 }}
        />
      ) : null}
      <Typography variant="body2" noWrap title={attachment.original_name || ''}>
        {attachment.original_name || 'Attachment'}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {formatBytes(attachment.size_bytes)}
        {failed ? ' · preview unavailable' : ''}
      </Typography>
      <Button size="small" onClick={download}>
        Download
      </Button>
    </Paper>
  );
}

function statusColor(status) {
  if (status === 'open') return 'warning';
  if (status === 'in_progress') return 'info';
  if (status === 'resolved') return 'success';
  return 'default';
}

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [comment, setComment] = useState('');
  const [queueRole, setQueueRole] = useState('');

  const detailQuery = useQuery(['ticket', id], async () => {
    const res = await ticketsAPI.get(id);
    return res.data.data;
  });

  const ticket = detailQuery.data;
  const canReassign = REASSIGN_ROLES.has(user?.role);
  const nextStatuses = ticket ? TRANSITIONS[ticket.status] || [] : [];

  const invalidate = () => {
    qc.invalidateQueries(['ticket', id]);
    qc.invalidateQueries(['tickets']);
    qc.invalidateQueries(['tickets-summary']);
  };

  const statusMut = useMutation(
    ({ status, comment: c }) => ticketsAPI.updateStatus(id, { status, comment: c }),
    {
      onSuccess: () => {
        toast.success('Status updated');
        setComment('');
        invalidate();
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
    }
  );

  const commentMut = useMutation((body) => ticketsAPI.addComment(id, { body }), {
    onSuccess: () => {
      toast.success('Comment added');
      setComment('');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const reassignMut = useMutation((body) => ticketsAPI.reassign(id, body), {
    onSuccess: () => {
      toast.success('Reassigned');
      setQueueRole('');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  if (detailQuery.isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Ticket not found</Typography>
        <Button onClick={() => navigate('/tickets')}>Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="overline">{ticket.ticket_number}</Typography>
          <Typography variant="h5">{ticket.subject}</Typography>
          <Stack direction="row" spacing={1} mt={1}>
            <Chip size="small" label={ticket.status} color={statusColor(ticket.status)} />
            <Chip size="small" label={ticket.priority} variant="outlined" />
            <Chip size="small" label={ticket.category} variant="outlined" />
          </Stack>
        </Box>
        <Button onClick={() => navigate('/tickets')}>Back</Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography whiteSpace="pre-wrap" mb={2}>
          {ticket.description}
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="body2">Student: {ticket.student_name || '—'}</Typography>
          <Typography variant="body2">Teacher: {ticket.teacher_name || '—'}</Typography>
          <Typography variant="body2">Created by: {ticket.created_by_name || '—'}</Typography>
          <Typography variant="body2">
            Assignee: {ticket.assignee_name?.trim() || ticket.queue_role || '—'}
          </Typography>
        </Stack>
      </Paper>

      {(ticket.attachments || []).length ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" mb={1}>
            Attachments
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {ticket.attachments.map((attachment) => (
              <TicketAttachmentCard key={attachment.id} ticketId={ticket.id} attachment={attachment} />
            ))}
          </Stack>
        </Paper>
      ) : null}

      {nextStatuses.length > 0 ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" mb={1}>
            Update status
          </Typography>
          <TextField
            label="Optional comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 1 }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {nextStatuses.map((s) => (
              <Button
                key={s}
                variant="contained"
                disabled={statusMut.isLoading}
                onClick={() => statusMut.mutate({ status: s, comment: comment || undefined })}
              >
                Mark {s.replace('_', ' ')}
              </Button>
            ))}
          </Stack>
        </Paper>
      ) : null}

      {canReassign ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" mb={1}>
            Reassign queue
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              select
              size="small"
              label="Queue role"
              value={queueRole}
              onChange={(e) => setQueueRole(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="teacher_admin">teacher_admin</MenuItem>
              <MenuItem value="finance_admin">finance_admin</MenuItem>
              <MenuItem value="sales_supervisor">sales_supervisor</MenuItem>
              <MenuItem value="batch_admin">batch_admin / supervisor</MenuItem>
              <MenuItem value="admin">admin</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              disabled={!queueRole || reassignMut.isLoading}
              onClick={() =>
                reassignMut.mutate({ queue_role: queueRole, assignee_user_id: null })
              }
            >
              Reassign to queue
            </Button>
          </Stack>
        </Paper>
      ) : null}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" mb={1}>
          Comments
        </Typography>
        <Stack spacing={1} mb={2}>
          {(ticket.comments || []).length === 0 ? (
            <Typography color="text.secondary">No comments yet.</Typography>
          ) : (
            (ticket.comments || []).map((c) => (
              <Box key={c.id}>
                <Typography variant="caption" color="text.secondary">
                  {c.author_name} · {new Date(c.created_at).toLocaleString()}
                </Typography>
                <Typography whiteSpace="pre-wrap">{c.body}</Typography>
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))
          )}
        </Stack>
        <TextField
          label="Add comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          sx={{ mb: 1 }}
        />
        <Button
          variant="outlined"
          disabled={!comment.trim() || commentMut.isLoading}
          onClick={() => commentMut.mutate(comment.trim())}
        >
          Post comment
        </Button>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" mb={1}>
          Timeline
        </Typography>
        <Stack spacing={1}>
          {(ticket.events || []).map((e) => (
            <Typography key={e.id} variant="body2">
              <strong>{e.event_type}</strong>
              {e.from_value || e.to_value ? ` · ${e.from_value || '—'} → ${e.to_value || '—'}` : ''}
              {e.actor_name ? ` · ${e.actor_name}` : ''}
              {' · '}
              {new Date(e.created_at).toLocaleString()}
            </Typography>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
